import { useEffect, useState, useRef } from "react";
import { buildCollectionReceiptPdf } from "@/lib/collectionReceiptPdf";
import { printPdf } from "@/lib/quotePdf";
import PdfPagesView from "@/components/PdfPagesView";
import api, { apiError, uploadFile } from "@/lib/api";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { TRY, fmtDate, todayISO, sameOrNew, docFileName } from "@/lib/format";
// Makbuz dosya adı: cari adının ilk iki kelimesi + makbuz no
const receiptFile = (r) => docFileName(r?.contact_title || r?.received_from || "Tahsilat Makbuzu", r?.receipt_no || "");
import { PageHeader, Empty, StatCard, Cell, Segmented, Panel } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, FileText, Loader2, ReceiptText, Download, Pencil, X, Search, Banknote, CreditCard, ArrowLeftRight, ScrollText, CalendarDays, Wallet, Link2, Eye, Printer } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";
import { Combobox } from "@/components/Combobox";
import { contactOptions } from "@/lib/contacts";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";

const METHODS = [
  { value: "nakit", label: "Nakit", icon: Banknote, stripe: "bg-[#16A34A]", cls: "border-emerald-300 bg-emerald-50 text-emerald-700" },
  { value: "havale", label: "Havale/EFT", icon: ArrowLeftRight, stripe: "bg-secondary", cls: "border-secondary/30 bg-secondary/5 text-secondary" },
  { value: "kredi_karti", label: "Kredi Kartı", icon: CreditCard, stripe: "bg-violet-500", cls: "border-violet-300 bg-violet-50 text-violet-700" },
  { value: "cek", label: "Çek", icon: ScrollText, stripe: "bg-amber-500", cls: "border-amber-300 bg-amber-50 text-amber-700" },
];
const methodOf = (m) => METHODS.find((x) => x.value === m) || { label: m, icon: Wallet, stripe: "bg-slate-400", cls: "" };

const blank = { contact_title: "", received_from: "", amount: "", method: "nakit", date: todayISO(), description: "", receipt_no: "", post_to_cari: true, invoice_no: "", invoice_id: "" };

export default function Receipts() {
  const [list, setList] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [printRec, setPrintRec] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [unpaid, setUnpaid] = useState([]);
  const [search, setSearch] = useState("");
  const [mFilter, setMFilter] = useState("all");
  const pdfRef = useRef(null);
  const [pdfData, setPdfData] = useState(null);
  // Önizleme = indirilen/yazdırılan PDF'in kendisi
  useEffect(() => {
    setPdfData(null); pdfRef.current = null;
    if (!printRec) return undefined;
    let off = false;
    buildCollectionReceiptPdf(printRec)
      .then((pdf) => { if (!off) { pdfRef.current = pdf; setPdfData(pdf.output("arraybuffer")); } })
      .catch((e) => { console.error(e); if (!off) toast.error("Makbuz önizlemesi oluşturulamadı"); });
    return () => { off = true; };
  }, [printRec]);

  const load = () => api.get("/receipts").then((r) => setList(sameOrNew(r.data))).catch(() => {});
  useAutoRefresh(load);
  useEffect(() => {
    load();
    api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = form.contact_title.trim();
    if (!t) { setUnpaid([]); return; }
    let active = true;
    api.get(`/invoices/unpaid?contact_title=${encodeURIComponent(t)}`)
      .then((r) => { if (active) setUnpaid(r.data); })
      .catch(() => { if (active) setUnpaid([]); });
    return () => { active = false; };
    // eslint-disable-next-line
  }, [form.contact_title]);

  const save = async () => {
    if (!form.contact_title.trim() && !form.received_from.trim()) return toast.error("Cari ünvanı veya kimden alındı gerekli");
    if (!(Number(form.amount) > 0)) return toast.error("Tutar girin");
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      const { data } = editId ? await api.put(`/receipts/${editId}`, payload) : await api.post("/receipts", payload);
      toast.success(editId ? "Makbuz güncellendi" : "Makbuz oluşturuldu");
      setForm(blank); setEditId(null);
      load();
      setPrintRec(data);
      if (!editId && data.invoice_id) attachToInvoice(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  // Faturaya bağlı makbuz: PDF'i üretip ilgili satış faturasının belgelerine ekle
  const attachToInvoice = async (r) => {
    try {
      const pdf = await buildCollectionReceiptPdf(r);
      const name = receiptFile(r);
      const file = new File([pdf.output("blob")], name, { type: "application/pdf" });
      const up = await uploadFile(file);
      await api.post(`/invoices/${r.invoice_id}/documents`, { url: up.url, name, content_type: "application/pdf" });
      toast.success(`Makbuz PDF'i ${r.invoice_no ? `#${r.invoice_no} ` : ""}faturasının belgelerine eklendi`);
    } catch (e) { toast.error("Makbuz faturaya eklenemedi: " + apiError(e)); }
  };

  const startEdit = (r) => {
    setEditId(r.id);
    setForm({
      contact_title: r.contact_title || "", received_from: r.received_from || "", amount: String(r.amount ?? ""),
      method: r.method || "nakit", date: r.date || todayISO(), description: r.description || "",
      receipt_no: r.receipt_no || "", post_to_cari: true, invoice_no: r.invoice_no || "", invoice_id: r.invoice_id || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditId(null); setForm(blank); };

  const del = async (id) => { if (await confirm("Makbuz silinsin mi?")) { await api.delete(`/receipts/${id}`); load(); } };

  const downloadReceiptPdf = async () => {
    if (!printRec) return;
    setDownloading(true);
    try {
      const pdf = pdfRef.current || await buildCollectionReceiptPdf(printRec);
      pdf.save(receiptFile(printRec));
      toast.success("Makbuz PDF indirildi");
    } catch (e) { console.error(e); toast.error("PDF oluşturulamadı"); }
    finally { setDownloading(false); }
  };

  const today = todayISO();
  const monthStart = `${today.slice(0, 7)}-01`;
  const sum = (arr) => arr.reduce((s, r) => s + Number(r.amount || 0), 0);
  const todayList = list.filter((r) => String(r.date || "").slice(0, 10) === today);
  const monthList = list.filter((r) => String(r.date || "").slice(0, 10) >= monthStart);
  const linked = list.filter((r) => r.invoice_id);
  const filtered = list.filter((r) => {
    if (mFilter !== "all" && r.method !== mFilter) return false;
    const s = search.trim().toLocaleLowerCase("tr");
    return !s || [r.contact_title, r.received_from, r.receipt_no, r.invoice_no, r.description].some((v) => String(v || "").toLocaleLowerCase("tr").includes(s));
  });
  const filterOptions = [["all", "Tümü", list.length], ...METHODS.map((m) => [m.value, m.label, list.filter((r) => r.method === m.value).length])];

  return (
    <div className="space-y-6 stagger-in" data-testid="receipts-page">
      <PageHeader title="Tahsilat Makbuzu" badge="Tahsilat" subtitle="Müşteriden alınan tahsilat için imza alanlı makbuz · faturaya bağlanırsa ödeme geçmişine ve belgelerine otomatik işlenir" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="receipt-kpis">
        <StatCard label="Bugün Tahsil Edilen" value={TRY(sum(todayList))} icon={CalendarDays} tone="success" sub={`${todayList.length} makbuz`} testid="receipt-kpi-today" />
        <StatCard label="Bu Ay Tahsil Edilen" value={TRY(sum(monthList))} icon={Wallet} tone="primary" sub={`${monthList.length} makbuz`} testid="receipt-kpi-month" />
        <StatCard label="Toplam Makbuz" value={list.length} icon={ReceiptText} sub={TRY(sum(list))} testid="receipt-kpi-total" />
        <StatCard label="Faturaya Bağlı" value={linked.length} icon={Link2} tone="blue" sub={`${list.length - linked.length} cariye mahsuben`} testid="receipt-kpi-linked" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Form */}
        <Panel className="lg:col-span-2 h-fit" title={editId ? "Makbuz Düzenle" : "Yeni Makbuz"} icon={ReceiptText} testid="receipt-form"
          actions={editId && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={cancelEdit} data-testid="rec-cancel-edit"><X className="h-3.5 w-3.5 mr-1" />Vazgeç</Button>}>
          <div className="space-y-4">
          <F label="Cari / Müşteri">
            <Combobox options={contactOptions(contacts)} value={form.contact_title} onChange={(v) => setForm({ ...form, contact_title: v })} allowCustom placeholder="Cari seçin veya yazın" searchPlaceholder="Ünvan / VKN / TCKN ara..." data-testid="rec-contact" />
          </F>
          <F label="Kimden Alındı (opsiyonel)"><Input value={form.received_from} onChange={(e) => setForm({ ...form, received_from: e.target.value })} placeholder="Teslim eden kişi" data-testid="rec-from" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Tutar (₺)"><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="font-head font-black text-lg tabular-nums" data-testid="rec-amount" /></F>
            <F label="Ödeme Yöntemi">
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="rec-method"><SelectValue /></SelectTrigger>
                <SelectContent>{METHODS.map((m) => <SelectItem key={m.value} value={m.value}><span className="inline-flex items-center gap-2"><m.icon className="h-3.5 w-3.5" />{m.label}</span></SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Tarih"><DatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v })} data-testid="rec-date" /></F>
            <F label="Makbuz No (opsiyonel)"><Input value={form.receipt_no} onChange={(e) => setForm({ ...form, receipt_no: e.target.value })} placeholder="Otomatik" data-testid="rec-no" /></F>
          </div>
          <F label="İlişkili Fatura (opsiyonel)">
            <Select
              value={form.invoice_id || "__none__"}
              onValueChange={(v) => {
                if (v === "__none__") setForm({ ...form, invoice_no: "", invoice_id: "" });
                else { const inv = unpaid.find((x) => x.id === v); setForm({ ...form, invoice_no: inv?.invoice_no || "", invoice_id: v }); }
              }}>
              <SelectTrigger data-testid="rec-invoice"><SelectValue placeholder="Fatura seçin" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Cariye mahsuben (fatura yok)</SelectItem>
                {unpaid.map((inv) => (
                  <SelectItem key={inv.id} value={inv.id} data-testid={`rec-invoice-${inv.id}`}>{inv.invoice_no || `Taslak · ${fmtDate(inv.date)}`} · {inv.type === "satis" ? "Satış" : "Alış"} · Kalan {TRY(inv.remaining)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.contact_title.trim() && unpaid.length === 0 && <p className="text-[11px] text-muted-foreground mt-1">Bu carinin ödenmemiş faturası yok — tahsilat "cariye mahsuben" olarak işlenir.</p>}
          </F>
          <F label="Açıklama"><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="rec-desc" /></F>
          <div className="flex items-center gap-3 bg-muted/50 rounded-lg p-3">
            <Switch checked={form.post_to_cari} onCheckedChange={(v) => setForm({ ...form, post_to_cari: v })} data-testid="rec-post-cari" />
            <div>
              <Label className="text-sm font-semibold">Cari tahsilatına da işle</Label>
              <p className="text-xs text-muted-foreground">{form.invoice_id ? "Fatura seçildi: tutar faturanın ödeme geçmişine işlenir ve cari bakiyeye fatura ödemesi olarak yansır (çift kayıt olmaz)." : "Açık ise bu tutar seçili carinin \"Ödemeler\" (tahsilat) kaydına eklenir."}</p>
            </div>
          </div>
          <Button className="w-full h-11" onClick={save} disabled={saving} data-testid="rec-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : (editId ? <Pencil className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />)}{editId ? "Değişiklikleri Kaydet" : "Makbuz Oluştur & Önizle"}
          </Button>
          </div>
        </Panel>

        {/* List */}
        <div className="lg:col-span-3 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Cari, makbuz no, fatura no veya açıklama ara..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10" data-testid="receipt-search" />
            </div>
            <Segmented value={mFilter} onChange={setMFilter} options={filterOptions} testid="receipt-filter" size="xs" />
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground px-1" data-testid="receipt-list-summary">
            <span><b className="text-foreground tabular-nums">{filtered.length}</b> makbuz listeleniyor</span>
            <span className="tabular-nums">Liste toplamı: <b className="text-[#16A34A]">{TRY(sum(filtered))}</b></span>
          </div>
          {filtered.length === 0 ? <Empty title={list.length ? "Eşleşen makbuz yok" : "Makbuz yok"} desc={list.length ? "Filtreyi veya aramayı değiştirin." : "İlk tahsilat makbuzunuzu oluşturun."} /> : (
            <div className="space-y-2.5" data-testid="receipt-list">
              {filtered.map((r) => {
                const m = methodOf(r.method);
                return (
                  <div key={r.id} className="group relative rounded-xl border bg-card pl-5 pr-4 py-3.5 overflow-hidden transition-all hover:shadow-md hover:-translate-y-px" data-testid={`receipt-${r.id}`}>
                    <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${m.stripe}`} />
                    <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                      <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-3 min-w-0">
                        <Cell label="Makbuz No">
                          <div className="font-mono text-sm font-bold tabular-nums">#{r.receipt_no || "—"}</div>
                          <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" />{fmtDate(r.date)}</div>
                        </Cell>
                        <Cell label="Cari / Kimden" className="col-span-2 md:col-span-1">
                          <div className="font-head font-black text-[15px] leading-tight truncate" title={r.contact_title || r.received_from}>{r.contact_title || r.received_from || "—"}</div>
                          {r.received_from && r.contact_title && <div className="text-[11px] text-muted-foreground truncate">Teslim eden: {r.received_from}</div>}
                        </Cell>
                        <Cell label="Yöntem · Fatura">
                          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${m.cls}`}><m.icon className="h-3 w-3" />{m.label}</span>
                          <div className="text-[11px] text-muted-foreground mt-0.5 truncate" title={r.invoice_no ? `Fatura #${r.invoice_no}` : undefined}>{r.invoice_no ? <span className="inline-flex items-center gap-1 text-secondary font-semibold"><Link2 className="h-3 w-3" />#{r.invoice_no}</span> : r.invoice_id ? "Taslak faturaya bağlı" : "Cariye mahsuben"}</div>
                        </Cell>
                        <Cell label="Açıklama">
                          <div className="text-xs text-muted-foreground line-clamp-2" title={r.description}>{r.description || "—"}</div>
                        </Cell>
                        <Cell label="Tutar">
                          <div className="font-head font-black text-[17px] tabular-nums text-[#16A34A]" data-testid={`receipt-amount-${r.id}`}>{TRY(r.amount)}</div>
                          {r.created_by && <div className="text-[10px] text-muted-foreground truncate">{r.created_by}</div>}
                        </Cell>
                      </div>
                      <div className="flex items-center gap-0.5 lg:justify-end shrink-0 lg:pl-3 lg:border-l">
                        <Button size="sm" variant="outline" className="h-8" onClick={() => setPrintRec(r)} data-testid={`receipt-pdf-${r.id}`}><Eye className="h-3.5 w-3.5 mr-1 text-secondary" />Önizle / PDF</Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEdit(r)} title="Düzenle" data-testid={`receipt-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => del(r.id)} title="Sil" data-testid={`receipt-del-${r.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Makbuz önizleme */}
      <Dialog open={!!printRec} onOpenChange={(o) => !o && setPrintRec(null)}>
        <DialogContent className="max-w-4xl w-[96vw] max-h-[94vh] overflow-y-auto p-0" data-testid="receipt-preview">
          <div className="sticky top-0 z-10 bg-card border-b p-4 pr-14 flex flex-wrap items-center gap-2">
            <DialogTitle className="font-head text-xl mr-auto">Makbuz Önizleme</DialogTitle>
            <Button size="sm" onClick={downloadReceiptPdf} disabled={downloading || !pdfData} data-testid="receipt-download-btn">{downloading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}PDF İndir</Button>
            <Button size="sm" variant="outline" onClick={() => pdfRef.current && printPdf(pdfRef.current, receiptFile(printRec).replace(/\.pdf$/, ""), { asFileName: true })} disabled={!pdfData} data-testid="receipt-print-btn"><Printer className="h-4 w-4 mr-1.5" />Yazdır</Button>
            <Button size="sm" variant="outline" onClick={() => setPrintRec(null)} data-testid="receipt-close-btn">Kapat</Button>
          </div>
          <div className="p-4 bg-slate-200/70 overflow-x-auto" data-testid="receipt-preview-pages">
            {pdfData ? <PdfPagesView data={pdfData} width={Math.min(794, (typeof window !== "undefined" ? window.innerWidth : 900) - 64)} testid="receipt-pdf-pages" /> : (
              <div className="py-24 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Makbuz hazırlanıyor…</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);
